import * as React from 'react';
import { Button, ButtonDropdown, Divider, Icon, ITooltipInheritedProps, SelectList, SelectListItem } from 'kui';
import { ICardActionsProps } from './types';
import './CardActions.scss';
import {
    ARCHIVE_CARD_ACTION,
    CARD_PREVIEW_ACTION,
    COPY_CARD_ACTION,
    DELETE_CARD_ACTION,
    DONE_CARD_ACTION,
    EDIT_CARD_ACTION,
    GET_LINK_CARD_ACTION,
    RENAME_CARD_ACTION,
    TOOLTIP_ACTIONS_CARD,
    UNARCHIVE_CARD_ACTION
} from '../../../wording';
import { CARD_ACTIONS_CLASS, CARD_CONTROL_CLASS, CARDS_DROPDOWN_SCROLL_CLASS } from '../../..';
import { ProFeatureIndicator } from '../../../../../base/components/ProFeatureIndicator/ProFeatureIndicator';
import { v4 as uuidv4 } from 'uuid';
import { EssentialFeatureIndicator } from '../../../../../base/components/EssentialFeatureIndicator/EssentialFeatureIndicator';
import { CardColorTagsActionHOC } from '../../CardColorTagsAction/hocs/CardColorTagsActionHOC';

export function CardActions({
    isArchive,
    allowArchive,
    portalId,
    canDone,
    cardId,
    isEssentialFeature,
    isReadonly,
    isProFeature,
    isShowCardPreview = true,
    isShowColorTags = true,
    onCardCopy,
    onCardArchive,
    onCardDelete,
    onClick,
    onUnarchive,
    onDone,
    onEdit,
    onOpenPreview,
    onRename,
    onGetLinkCard,
    openColorTags,
}: ICardActionsProps) {
    const classNameButton = CARD_ACTIONS_CLASS + '-button';
    const classDropdown = CARD_ACTIONS_CLASS + '-dropdown';

    const [classUnique] = React.useState(classNameButton + uuidv4());
    const [isOpened, setOpened] = React.useState(null);

    const onClose = () => {
        setOpened(false);
    };

    const onActionClick = (
        func: () => void
    ) => {
        const button = document.querySelector('.' + classUnique) as HTMLElement;
        if (button) {
            button.focus();
        }
        if (func) {
            onClose();
            func();
        }
    };

    React.useEffect(() => {
        if (isOpened) {
            onActionClick(null); // хз почему фокус уходит на body
            window.addEventListener('scroll', onScroll);
        }
        function onScroll() {
            onClose();
        }

        return () => window.removeEventListener('scroll', onScroll);
    }, [isOpened]);

    const PreviewSettingsIcon = require('!!@svgr/webpack!./preview-settings.svg').default;
    const UnarchiveIcon = require('!!@svgr/webpack!../../../../../../../../../../assets/svg/unarchive.svg').default;

    return (
        <ButtonDropdown
            className={`
                ${CARD_ACTIONS_CLASS}
                ${CARD_CONTROL_CLASS}
            `}
            directionHorizontal={'right'}
            dropdownClassName={`
                ${CARDS_DROPDOWN_SCROLL_CLASS}
                ${classDropdown}
                ${CARD_CONTROL_CLASS} handle-click-ignore`}
            isFitWindow
            isMoveToFit
            isScaleAnimation
            onClick={onClick}
            single={false}
            multiple
            opened={isOpened}
            onClose={onClose}
            onOpen={() => {
                setOpened(true);
                setTimeout(()=>{
                    const button = document.querySelector('.' + classUnique) as HTMLElement;
                    if (button) button.focus();
                }, 100);
            }}
            portal
            portalId={portalId}
        >
            <Button
                className={`${classNameButton} ${classUnique}`}
                tooltip={isOpened ?
                        null : {
                        isNoEvents: true,
                        isNoWrap: true,
                        value: TOOLTIP_ACTIONS_CARD
                    } as ITooltipInheritedProps
                }
                variant={'icon'}
            >
                <Icon xlink={'more'} size={24}/>
            </Button>
            <SelectList fixActive={false} className={'handle-click-ignore'}>
                {!isArchive && !isReadonly && isShowColorTags &&
                    <CardColorTagsActionHOC
                        cardId={cardId}
                        openColorTags={() => {
                            onClose();
                            openColorTags();
                        }}
                    />
                }
                {!isArchive && !isReadonly &&
                    <SelectListItem
                        icon={'rename'}
                        onClick={() => onActionClick(onRename)}
                        iconSize={24}
                    >
                        {RENAME_CARD_ACTION}
                    </SelectListItem>
                }
                {!isArchive && !isReadonly &&
                    <SelectListItem
                        icon={'pen'}
                        onClick={() => onActionClick(onEdit)}
                        iconSize={24}
                    >
                        {EDIT_CARD_ACTION}
                    </SelectListItem>
                }

                {!isArchive && !isReadonly &&
                    <Divider />
                }

                {canDone && !isArchive && !isReadonly &&
                    <SelectListItem
                        className={CARD_ACTIONS_CLASS + '-progress'}
                        icon={'progress'}
                        onClick={() => onActionClick(onDone)}
                        iconSize={24}
                    >
                        {DONE_CARD_ACTION}
                    </SelectListItem>
                }
                {!isArchive && !isReadonly && canDone && <Divider />}
                {!isArchive && !isReadonly &&
                    <SelectListItem
                        icon={'link'}
                        onClick={() => onActionClick(onGetLinkCard)}
                        iconSize={24}
                    >
                        {GET_LINK_CARD_ACTION}
                    </SelectListItem>
                }

                {!isArchive && !isReadonly &&
                    <Divider />
                }

                {!isArchive && !isReadonly &&
                    <SelectListItem
                        icon={'copy'}
                        onClick={() => onActionClick(onCardCopy)}
                        iconSize={24}
                    >
                        {COPY_CARD_ACTION}
                    </SelectListItem>
                }
                {isArchive && !isReadonly &&
                    <SelectListItem
                        className={CARD_ACTIONS_CLASS + '-item--unarchive'}
                        onClick={onUnarchive}
                        iconSize={24}
                    >
                        <UnarchiveIcon className={`${CARD_ACTIONS_CLASS}-item-icon`} />
                        {UNARCHIVE_CARD_ACTION}
                    </SelectListItem>
                }
                {!isArchive && !isReadonly &&
                    <SelectListItem
                        icon={'archive'}
                        onClick={onCardArchive}
                        iconSize={24}
                        className={`
                                ${!allowArchive && !isProFeature && !isEssentialFeature ? 'disabled' : ''}
                                ${isProFeature || isEssentialFeature ? CARD_ACTIONS_CLASS + '-item--indicator' : ''}
                            `}
                    >
                        <>
                            {ARCHIVE_CARD_ACTION}
                            {isProFeature && <ProFeatureIndicator />}
                            {isEssentialFeature && <EssentialFeatureIndicator />}
                        </>
                    </SelectListItem>
                }
                {!isReadonly &&
                    <SelectListItem
                        icon={'delete'}
                        onClick={onCardDelete}
                        iconSize={24}
                    >
                        {DELETE_CARD_ACTION}
                    </SelectListItem>
                }
                {isShowCardPreview && !isReadonly && !isArchive &&
                        <Divider />
                }
                {isShowCardPreview && !isReadonly && !isArchive &&
                    <SelectListItem
                        className={CARD_ACTIONS_CLASS + '-item--preview'}
                        onClick={() => onActionClick(onOpenPreview)}
                    >
                        <PreviewSettingsIcon className={`${CARD_ACTIONS_CLASS}-item-icon`} />
                        {CARD_PREVIEW_ACTION}
                    </SelectListItem>
                }
            </SelectList>
        </ButtonDropdown>
    );
}
